import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { Button, Dialog, Flex, Heading } from '@ifood/pomodoro-components';
import { graphql } from 'gatsby';
import React, { Children, FC, useState } from 'react';
import Slider from 'react-slick';

import { useMDXNode } from 'hooks/mdx';
import LandingPageLayout from 'modules/Core/layouts/landing-page';
import { MarkdownProcessor, addAnchorLinkToHeadings } from 'modules/Markdown';
import { BlankButton, Image, Link } from 'modules/UI';
import { DynamicPagesDataQuery } from 'types/codegen';

import {
  Background,
  Banner,
  Block,
  BlockIcon,
  Blocks,
  CheckItem,
  CheckList,
  Column,
  Container,
  Label,
  ObjectContainer,
  ObjectImage,
  Section,
  ServiceBlock,
  ServiceImage,
  SmallText,
  SubTitle,
  Text,
  VideoSrc,
} from './styles';

type PropTypes = {
  data: DynamicPagesDataQuery;
};

type DialogActionProps = {
  type: string;
  title: string;
  width: string;
  height?: string;
  src: string;
};

type PassOnClickToChildProps = {
  onClick: () => void;
};

const settings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  className: 'embeddables-carousel',
};

const SliderWithConfigs: FC = ({ children }) => (
  <div style={{ maxWidth: 1130, padding: '0 40px 40px 0', marginTop: 32 }}>
    <Slider {...settings}>{children}</Slider>
  </div>
);

const DialogAction: FC<DialogActionProps> = ({ type, title, width, height, src, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const PassOnClickToChild: FC<PassOnClickToChildProps> = ({ children, onClick }) => {
    return (
      <>
        {Children.toArray(children).map((child, index) => {
          return (
            <BlankButton key={index} onClick={onClick}>
              {child}
            </BlankButton>
          );
        })}
      </>
    );
  };

  return (
    <>
      <PassOnClickToChild onClick={(): void => setIsOpen(true)}>{children}</PassOnClickToChild>
      <Dialog open={isOpen} onClose={(): void => setIsOpen(false)} size="xl">
        <Dialog.Body padding="20px 32px">
          {type === 'iframe' && <iframe frameBorder={0} title={title} width={width} height={height} src={src} />}
          {type === 'video' && <video src={src} autoPlay muted loop width="100%" />}
        </Dialog.Body>
      </Dialog>
    </>
  );
};

type LinkButtonProps = {
  href?: string;
};

const LinkButton: FC<LinkButtonProps> = ({ children, href }) => {
  return (
    <Link dataTestId="md-link" href={href}>
      <Button>{children}</Button>
    </Link>
  );
};

const MarkDownOverrides = {
  h2: Heading,
  h3: SubTitle,
  p: Text,
  Container,
  Banner,
  Column,
  VideoBackground: Background,
  video: VideoSrc,
  a: LinkButton,
  ObjectContainer,
  ObjectImage,
  Section,
  img: Image,
  ul: CheckList,
  li: CheckItem,
  Label,
  Blocks,
  Block,
  BlockIcon,
  SmallText,
  ServiceBlock,
  ServiceImage,
  Flex,
  DialogAction,
  Slider: SliderWithConfigs,
};

const EmbeddablesTemplate = ({ data }: PropTypes): JSX.Element => {
  const ownNode = useMDXNode(data);

  const title = ownNode.frontmatter?.title;
  const description = ownNode.frontmatter?.description;

  return (
    <LandingPageLayout title={title} description={description}>
      <MarkdownProcessor
        components={MarkDownOverrides}
        decorateComponents={addAnchorLinkToHeadings}
        markup={ownNode.body}
      />
    </LandingPageLayout>
  );
};

export default EmbeddablesTemplate;

export const query = graphql`
  query EmbeddablesBodyQuery($pageId: String!) {
    allMdx(filter: { fields: { pageId: { eq: $pageId } } }) {
      nodes {
        body
        fields {
          filename
        }
        frontmatter {
          title
          description
        }
      }
    }
  }
`;
